import type { ContractChangeStatusEnum } from 'gql/graphql';

import type { MutableRefObject } from 'react';
import { useImperativeHandle } from 'react';

import { AgreementFormTopPanel } from '../AgreementFormTopPanel';
import { AgreementPriceListTable } from '../AgreementPriceListTable';
import { AgreementsAddRatio } from '../AgreementsAddRatio';
import { useGetAgreementRatioItems } from '../hooks/useGetAgreementRatioItems';
import { useRemoveAgreementsRatio } from '../hooks/useRemoveAgreementsRatio';

const noDataComp = () => 'Для отображения данных добавьте коэффициенты';

export const AgreementPriceListRatio = ({
    agreementId,
    refreshData,
    disabledByStatus,
    infoText = undefined,
    needUpdateCurrentPriceList,
    needUpdatePriceListHandler,
    isDisabledByProjectStatus,
    contractChangeStatus,
    isDisabledByContractStatus,
    agreementTableApiRef,
    hasItemRow,
}: {
    agreementId: number;
    refreshData: () => void;
    disabledByStatus: boolean;
    isDisabledByProjectStatus: boolean;
    isDisabledByContractStatus: boolean;
    infoText?: string;
    needUpdateCurrentPriceList?: boolean;
    contractChangeStatus?: ContractChangeStatusEnum;
    needUpdatePriceListHandler?: (nextState: boolean) => void;
    agreementTableApiRef: MutableRefObject<{ refreshCurrentTable: (skipCheckedReset?: boolean) => void } | undefined>;
    hasItemRow: boolean;
}) => {
    const {
        datasource,
        columns,
        columnsForTableSettings,
        requestStatus,
        isColumnVisible,
        handleTableSettingsConfirm,
        rowCount,
        totalCount,
        refreshAgreementsRatio,
        checkedIdsArr,
        handleClearChecked,
        refreshFilters,
        tableRef,
    } = useGetAgreementRatioItems({
        contractChangeId: agreementId,
        refreshData,
        disabledByStatus,
        isDisabledByProjectStatus,
        needUpdateCurrentPriceList,
        contractChangeStatus,
        needUpdatePriceListHandler,
    });

    useImperativeHandle(agreementTableApiRef, () => ({ refreshCurrentTable: refreshAgreementsRatio }), [
        refreshAgreementsRatio,
    ]);

    const {
        removeRatioContractChangeData,
        removeRatioContractChangeStatus,
        removeRatioContractChangeError,
        removeRatioContractChangeSetData,
        handleRemoveRatioContractChange,
    } = useRemoveAgreementsRatio(agreementId, checkedIdsArr, refreshAgreementsRatio, refreshFilters);

    return (
        <>
            <AgreementFormTopPanel infoText={infoText}>
                <AgreementsAddRatio
                    disabledByStatus={disabledByStatus}
                    refreshAgreementsRatio={refreshAgreementsRatio}
                    isDisabledByProjectStatus={isDisabledByProjectStatus}
                    isDisabledByContractStatus={isDisabledByContractStatus}
                    refreshFilters={refreshFilters}
                />
            </AgreementFormTopPanel>
            <AgreementPriceListTable
                refreshCurrentTableRef={tableRef}
                rowCount={rowCount}
                totalCount={totalCount}
                columnsForTableSettings={columnsForTableSettings}
                handleTableSettingsConfirm={handleTableSettingsConfirm}
                isColumnVisible={isColumnVisible}
                datasource={datasource}
                columns={columns}
                requestStatus={requestStatus}
                checkedIdsArr={checkedIdsArr}
                handleClearChecked={handleClearChecked}
                removeRowsData={removeRatioContractChangeData}
                removeRowsStatus={removeRatioContractChangeStatus}
                removeRowsError={removeRatioContractChangeError}
                removeRowsSetData={removeRatioContractChangeSetData}
                handleRemoveRows={handleRemoveRatioContractChange}
                tableContext={{ tableUniqueId: 'agreementRatioItems' }}
                emptyDataComponent={noDataComp}
                hasItemRow={hasItemRow}
            />
        </>
    );
};

AgreementPriceListRatio.displayName = 'AgreementPriceListRatio';
